import React, { createContext, useState, useEffect, useCallback } from "react";
import { configureApi, setApiLoggingOut } from "../utils/apiClient";
import { initializeApiMiddleware, setLoggingOutState } from "../utils/apiMiddleware";
import { initUserActivityTracking, cleanupUserActivityTracking, resetUserActivityTracking } from "../utils/userActivity";
import { refreshAuthTokens } from "../services/authAPI";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return JSON.parse(localStorage.getItem("isLoggedIn")) || false;
  });
  const [token, setToken] = useState(() => {
    return localStorage.getItem("token") || "";
  });
  const [refreshToken, setRefreshToken] = useState(() => {
    return localStorage.getItem("refreshToken") || "";
  });
  const [member, setMember] = useState(() => {
    return JSON.parse(localStorage.getItem("member")) || {};
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    return JSON.parse(localStorage.getItem("isAdmin")) || false;
  });
  const [isServiceProvider, setIsServiceProvider] = useState(() => {
    return JSON.parse(localStorage.getItem("isServiceProvider")) || false;
  });
  const [globalError, setGlobalError] = useState("");
  const [apiConfigured, setApiConfigured] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const updateToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem("token", newToken);
  };

  const updateRefreshToken = (newRefreshToken) => {
    setRefreshToken(newRefreshToken);
    localStorage.setItem("refreshToken", newRefreshToken);
  };

  const updateTokens = useCallback((accessToken, refreshTokenValue) => {
    setToken(accessToken);
    setRefreshToken(refreshTokenValue);
    localStorage.setItem("token", accessToken);
    localStorage.setItem("refreshToken", refreshTokenValue);
  }, []);

  const logout = useCallback((skipRedirect = false) => {
    
    // Set logging out state to prevent further requests
    setLoggingOutState(true);
    setApiLoggingOut(true);
    
    setIsLoggedIn(false);
    setToken("");
    setRefreshToken("");
    setMember({});
    setIsAdmin(false);
    setIsServiceProvider(false);
    setHasFetched(false); // Reset hasFetched so data will be refetched on next login
    setResetTrigger(prev => prev + 1); // Trigger reset in other contexts
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("member");
    localStorage.removeItem("isAdmin");
    localStorage.removeItem("isServiceProvider");
    
    // Reset activity tracking on logout
    resetUserActivityTracking();
    
    if (!skipRedirect) {
      window.location.href = "/login";
    }
  }, []);

  // Function to refresh tokens
  const refreshTokens = useCallback(async () => {
    
    if (!refreshToken) {
      console.error('❌ [AUTH CONTEXT] No refresh token available');
      throw new Error('No refresh token available');
    }
    
    const tokens = await refreshAuthTokens(refreshToken);
    updateTokens(tokens.access_token, tokens.refresh_token);
    
    return tokens.access_token;
  }, [refreshToken, updateTokens]);

  // Configure API with current token and authentication handlers
  useEffect(() => {
    if (token) {
      configureApi(token, updateToken, logout);
      setApiConfigured(true);
    } else {
      setApiConfigured(false);
    }
    
    // Initialize API middleware with refresh capabilities
    initializeApiMiddleware(logout, refreshTokens);
  }, [token, refreshToken, refreshTokens, logout]);

  // Initialize user activity tracking
  useEffect(() => {
    initUserActivityTracking();
    
    // Cleanup on unmount
    return () => {
      cleanupUserActivityTracking();
    };
  }, []);

  // Reset activity tracking when user logs in
  useEffect(() => {
    if (isLoggedIn && token) {
      resetUserActivityTracking();
    }
  }, [isLoggedIn, token]);

  useEffect(() => {
    localStorage.setItem("isLoggedIn", JSON.stringify(isLoggedIn));
    localStorage.setItem("member", JSON.stringify(member));
    localStorage.setItem("isAdmin", JSON.stringify(isAdmin));
    localStorage.setItem(
      "isServiceProvider",
      JSON.stringify(isServiceProvider)
    );
  }, [isLoggedIn, member, isAdmin, isServiceProvider]);

  // Reset trigger when user logs in
  useEffect(() => {
    if (isLoggedIn && token && resetTrigger > 0) {
      setResetTrigger(0);
    }
  }, [isLoggedIn, token, resetTrigger]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        setIsLoggedIn,
        token,
        setToken: updateToken,
        refreshToken,
        setRefreshToken: updateRefreshToken,
        member,
        setMember,
        updateToken,
        updateRefreshToken,
        updateTokens,
        refreshTokens,
        isAdmin,
        setIsAdmin,
        isServiceProvider,
        setIsServiceProvider,
        globalError,
        setGlobalError,
        logout,
        apiConfigured,
        hasFetched,
        setHasFetched,
        resetTrigger,
        setResetTrigger,
        refreshTrigger,
        setRefreshTrigger,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};